<template>
  <div class="mobile-admin-student-page">
    <van-nav-bar title="学员管理" fixed placeholder />

    <van-pull-refresh v-model="refreshing" @refresh="handleRefresh">
      <div class="page-content">
        <!-- 顶部搜索筛选区 -->
        <div class="search-card">
          <van-field
              v-model="searchForm.username"
              label="账号"
              placeholder="请输入账号"
              clearable
          />

          <van-field
              v-model="searchForm.realName"
              label="姓名"
              placeholder="请输入姓名"
              clearable
          />

          <div class="filter-row">
            <div class="filter-item" @click="showSearchSubjectPicker = true">
              <span class="filter-label">科目</span>
              <span class="filter-value">
                {{ searchSubjectText || '全部科目' }}
              </span>
            </div>

            <div class="filter-item" @click="showSearchStatusPicker = true">
              <span class="filter-label">状态</span>
              <span class="filter-value">
                {{ searchStatusText || '全部状态' }}
              </span>
            </div>
          </div>

          <div class="search-actions">
            <van-button
                round
                plain
                block
                type="default"
                size="small"
                @click="resetSearch"
            >
              重置
            </van-button>

            <van-button
                round
                block
                type="info"
                size="small"
                @click="submitSearch"
            >
              查询
            </van-button>
          </div>
        </div>

        <van-button
            type="info"
            block
            round
            class="add-btn"
            @click="openAddPopup"
        >
          新增学员
        </van-button>

        <div class="summary-row">
          共 {{ pagination.total }} 名学员
        </div>

        <template v-if="studentList.length > 0">
          <div
              v-for="item in studentList"
              :key="item.id"
              class="student-card"
          >
            <div class="card-top">
              <div class="student-main">
                <div class="student-name">
                  {{ item.realName || '-' }}
                </div>
                <div class="student-username">
                  账号：{{ item.username || '-' }}
                </div>
              </div>

              <van-tag :type="item.status === 1 ? 'success' : 'danger'">
                {{ item.status === 1 ? '启用' : '禁用' }}
              </van-tag>
            </div>

            <div class="info-line">
              <span>科目：</span>{{ item.subjectName || getSubjectName(item.subjectMappingId) || '-' }}
            </div>
            <div class="info-line">
              <span>最后登录：</span>{{ item.lastLoginTime || '-' }}
            </div>
            <div class="info-line">
              <span>创建时间：</span>{{ item.createTime || '-' }}
            </div>

            <div class="action-grid">
              <van-button
                  size="small"
                  plain
                  type="info"
                  @click="handleLearning(item)"
              >
                学习情况
              </van-button>

              <van-button
                  size="small"
                  plain
                  type="info"
                  @click="openEditPopup(item)"
              >
                编辑
              </van-button>

              <van-button
                  size="small"
                  plain
                  :type="item.status === 1 ? 'warning' : 'success'"
                  @click="handleChangeStatus(item)"
              >
                {{ item.status === 1 ? '禁用' : '启用' }}
              </van-button>

              <van-button
                  size="small"
                  plain
                  type="warning"
                  @click="handleResetPassword(item)"
              >
                重置密码
              </van-button>

              <van-button
                  size="small"
                  plain
                  type="danger"
                  @click="handleDelete(item)"
              >
                删除
              </van-button>
            </div>
          </div>

          <!-- 分页 -->
          <div v-if="pagination.total > pagination.pageSize" class="pagination-box">
            <van-pagination
                v-model="pagination.pageNum"
                :total-items="pagination.total"
                :items-per-page="pagination.pageSize"
                mode="simple"
                @change="handlePageChange"
            />
          </div>
        </template>

        <van-empty
            v-else
            description="暂无学员"
        />
      </div>
    </van-pull-refresh>

    <!-- 新增学员弹窗 -->
    <van-popup
        v-model="addPopupVisible"
        position="bottom"
        round
        closeable
        :style="{ height: '78%' }"
    >
      <div class="popup-content">
        <div class="popup-title">新增学员</div>

        <van-form @submit="submitAddStudent">
          <van-field
              v-model="addForm.username"
              label="账号"
              placeholder="请输入账号"
              :rules="[{ required: true, message: '请输入账号' }]"
          />

          <van-field
              v-model="addForm.realName"
              label="姓名"
              placeholder="请输入姓名"
              :rules="[{ required: true, message: '请输入姓名' }]"
          />

          <van-field
              v-model="addForm.password"
              label="密码"
              type="password"
              placeholder="请输入初始密码"
              :rules="[{ required: true, message: '请输入初始密码' }]"
          />

          <van-field
              readonly
              clickable
              label="所属科目"
              :value="addSubjectText"
              placeholder="请选择科目"
              :rules="[{ required: true, message: '请选择科目' }]"
              @click="showAddSubjectPicker = true"
          />

          <div class="submit-area">
            <van-button
                round
                block
                type="info"
                native-type="submit"
                :loading="submitting"
            >
              确认新增
            </van-button>
          </div>
        </van-form>
      </div>
    </van-popup>

    <!-- 编辑学员弹窗 -->
    <van-popup
        v-model="editPopupVisible"
        position="bottom"
        round
        closeable
        :style="{ height: '64%' }"
    >
      <div class="popup-content">
        <div class="popup-title">编辑学员</div>

        <van-form @submit="submitEditStudent">
          <van-field
              v-model="editForm.username"
              label="账号"
              disabled
          />

          <van-field
              v-model="editForm.realName"
              label="姓名"
              placeholder="请输入姓名"
              :rules="[{ required: true, message: '请输入姓名' }]"
          />

          <van-field
              readonly
              clickable
              label="所属科目"
              :value="editSubjectText"
              placeholder="请选择科目"
              :rules="[{ required: true, message: '请选择科目' }]"
              @click="showEditSubjectPicker = true"
          />

          <div class="submit-area">
            <van-button
                round
                block
                type="info"
                native-type="submit"
                :loading="submitting"
            >
              保存修改
            </van-button>
          </div>
        </van-form>
      </div>
    </van-popup>

    <!-- 科目选择：搜索筛选 -->
    <van-popup v-model="showSearchSubjectPicker" position="bottom">
      <van-picker
          show-toolbar
          title="选择科目"
          :columns="subjectPickerColumnsWithAll"
          @confirm="onSearchSubjectConfirm"
          @cancel="showSearchSubjectPicker = false"
      />
    </van-popup>

    <!-- 科目选择：新增 -->
    <van-popup v-model="showAddSubjectPicker" position="bottom">
      <van-picker
          show-toolbar
          title="选择科目"
          :columns="subjectPickerColumns"
          @confirm="onAddSubjectConfirm"
          @cancel="showAddSubjectPicker = false"
      />
    </van-popup>

    <!-- 科目选择：编辑 -->
    <van-popup v-model="showEditSubjectPicker" position="bottom">
      <van-picker
          show-toolbar
          title="选择科目"
          :columns="subjectPickerColumns"
          @confirm="onEditSubjectConfirm"
          @cancel="showEditSubjectPicker = false"
      />
    </van-popup>

    <!-- 状态选择：搜索筛选 -->
    <van-popup v-model="showSearchStatusPicker" position="bottom">
      <van-picker
          show-toolbar
          title="选择状态"
          :columns="statusPickerColumns"
          @confirm="onSearchStatusConfirm"
          @cancel="showSearchStatusPicker = false"
      />
    </van-popup>
  </div>
</template>

<script>
import { Toast, Dialog } from 'vant'
import {
  getStudentList,
  addStudent,
  updateStudent,
  updateStudentStatus,
  resetStudentPassword,
  deleteStudent
} from '@/api/admin/student'
import { getSubjectMappingOptions } from '@/api/admin/subjectMapping'

export default {
  name: 'MobileAdminStudent',

  data() {
    return {
      loading: false,
      refreshing: false,
      submitting: false,

      studentList: [],
      subjectOptions: [],

      pagination: {
        pageNum: 1,
        pageSize: 4,
        total: 0
      },

      searchForm: {
        username: '',
        realName: '',
        subjectMappingId: null,
        status: null
      },

      addPopupVisible: false,
      editPopupVisible: false,

      showSearchSubjectPicker: false,
      showAddSubjectPicker: false,
      showEditSubjectPicker: false,
      showSearchStatusPicker: false,

      addForm: {
        username: '',
        realName: '',
        password: '',
        subjectMappingId: null
      },

      editForm: {
        id: null,
        username: '',
        realName: '',
        subjectMappingId: null,
        status: null
      },

      statusPickerColumns: [
        { text: '全部状态', value: null },
        { text: '启用', value: 1 },
        { text: '禁用', value: 0 }
      ]
    }
  },

  computed: {
    subjectPickerColumns() {
      return this.subjectOptions.map(item => ({
        text: item.subjectName,
        value: item.id
      }))
    },

    subjectPickerColumnsWithAll() {
      return [
        { text: '全部科目', value: null },
        ...this.subjectPickerColumns
      ]
    },

    searchSubjectText() {
      if (!this.searchForm.subjectMappingId) {
        return ''
      }

      return this.getSubjectName(this.searchForm.subjectMappingId)
    },

    searchStatusText() {
      if (this.searchForm.status === 1) {
        return '启用'
      }

      if (this.searchForm.status === 0) {
        return '禁用'
      }

      return ''
    },

    addSubjectText() {
      return this.getSubjectName(this.addForm.subjectMappingId)
    },

    editSubjectText() {
      return this.getSubjectName(this.editForm.subjectMappingId)
    }
  },

  created() {
    this.loadSubjectOptions()
    this.loadStudentList(true)
  },

  methods: {
    async loadSubjectOptions() {
      try {
        const res = await getSubjectMappingOptions()

        if (res.code === 200) {
          this.subjectOptions = res.data || []
        } else {
          Toast.fail(res.message || '获取科目失败')
        }
      } catch (e) {
        Toast.fail('获取科目失败')
      }
    },

    async handleRefresh() {
      await this.loadStudentList(true)
      this.refreshing = false
    },

    handlePageChange(page) {
      this.pagination.pageNum = page
      this.loadStudentList(false)
    },

    async loadStudentList(reset) {
      if (this.loading) {
        return
      }

      if (reset) {
        this.pagination.pageNum = 1
      }

      this.loading = true

      try {
        const params = {
          username: this.searchForm.username,
          realName: this.searchForm.realName,
          subjectMappingId: this.searchForm.subjectMappingId,
          status: this.searchForm.status,
          pageNum: this.pagination.pageNum,
          pageSize: this.pagination.pageSize
        }

        const res = await getStudentList(params)

        if (res.code === 200) {
          const data = res.data || {}

          this.studentList = data.records || []
          this.pagination.total = data.total || 0
        } else {
          Toast.fail(res.message || '获取学员列表失败')
        }
      } catch (e) {
        Toast.fail('获取学员列表失败')
      } finally {
        this.loading = false
      }
    },

    submitSearch() {
      this.loadStudentList(true)
    },

    resetSearch() {
      this.searchForm = {
        username: '',
        realName: '',
        subjectMappingId: null,
        status: null
      }

      this.loadStudentList(true)
    },

    openAddPopup() {
      this.addForm = {
        username: '',
        realName: '',
        password: '',
        subjectMappingId: null
      }

      this.addPopupVisible = true
    },

    openEditPopup(row) {
      this.editForm = {
        id: row.id,
        username: row.username,
        realName: row.realName,
        subjectMappingId: row.subjectMappingId,
        status: row.status
      }

      this.editPopupVisible = true
    },

    async submitAddStudent() {
      if (!this.addForm.subjectMappingId) {
        Toast('请选择科目')
        return
      }

      this.submitting = true

      try {
        const res = await addStudent(this.addForm)

        if (res.code === 200) {
          Toast.success('新增成功')
          this.addPopupVisible = false
          await this.loadStudentList(false)
        } else {
          Toast.fail(res.message || '新增失败')
        }
      } catch (e) {
        Toast.fail('新增失败')
      } finally {
        this.submitting = false
      }
    },

    async submitEditStudent() {
      if (!this.editForm.id) {
        Toast.fail('学员ID不能为空')
        return
      }

      if (!this.editForm.subjectMappingId) {
        Toast('请选择科目')
        return
      }

      this.submitting = true

      try {
        const res = await updateStudent(this.editForm)

        if (res.code === 200) {
          Toast.success('修改成功')
          this.editPopupVisible = false
          await this.loadStudentList(false)
        } else {
          Toast.fail(res.message || '修改失败')
        }
      } catch (e) {
        Toast.fail('修改失败')
      } finally {
        this.submitting = false
      }
    },

    async handleChangeStatus(row) {
      const nextStatus = row.status === 1 ? 0 : 1
      const actionText = nextStatus === 1 ? '启用' : '禁用'

      try {
        await Dialog.confirm({
          title: `${actionText}学员`,
          message: `确定要${actionText}学员「${row.realName || row.username}」吗？`
        })

        const res = await updateStudentStatus({
          id: row.id,
          status: nextStatus
        })

        if (res.code === 200) {
          Toast.success(`${actionText}成功`)
          await this.loadStudentList(false)
        } else {
          Toast.fail(res.message || `${actionText}失败`)
        }
      } catch (e) {
        // 用户取消
      }
    },

    async handleResetPassword(row) {
      try {
        await Dialog.confirm({
          title: '重置密码',
          message: `确定要将学员「${row.realName || row.username}」的密码重置为默认密码吗？`
        })

        const res = await resetStudentPassword(row.username)

        if (res.code === 200) {
          Toast.success('密码已重置为 123456')
        } else {
          Toast.fail(res.message || '重置密码失败')
        }
      } catch (e) {
        // 用户取消
      }
    },

    async handleDelete(row) {
      try {
        await Dialog.confirm({
          title: '删除学员',
          message: `删除学员「${row.realName || row.username}」会清理该学员相关学习数据，确定继续吗？`,
          confirmButtonText: '确认删除',
          cancelButtonText: '取消'
        })

        const res = await deleteStudent(row.id)

        if (res.code === 200) {
          Toast.success('删除成功')

          if (this.studentList.length === 1 && this.pagination.pageNum > 1) {
            this.pagination.pageNum -= 1
          }

          await this.loadStudentList(false)
        } else {
          Toast.fail(res.message || '删除失败')
        }
      } catch (e) {
        // 用户取消
      }
    },

    handleLearning(row) {
      this.$router.push({
        path: '/m/admin/student/learning-detail',
        query: {
          id: row.id,
          realName: row.realName || '',
          username: row.username || ''
        }
      })
    },

    getSubjectName(subjectMappingId) {
      if (!subjectMappingId) {
        return ''
      }

      const item = this.subjectOptions.find(i => Number(i.id) === Number(subjectMappingId))
      return item ? item.subjectName : ''
    },

    onSearchSubjectConfirm(item) {
      this.searchForm.subjectMappingId = item.value
      this.showSearchSubjectPicker = false
    },

    onAddSubjectConfirm(item) {
      this.addForm.subjectMappingId = item.value
      this.showAddSubjectPicker = false
    },

    onEditSubjectConfirm(item) {
      this.editForm.subjectMappingId = item.value
      this.showEditSubjectPicker = false
    },

    onSearchStatusConfirm(item) {
      this.searchForm.status = item.value
      this.showSearchStatusPicker = false
    }
  }
}
</script>

<style scoped>
.mobile-admin-student-page {
  min-height: 100vh;
  background: #f5f7fa;
}

.page-content {
  padding: 12px;
  box-sizing: border-box;
}

.search-card {
  background: #fff;
  border-radius: 12px;
  margin-bottom: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
}

.filter-row {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  border-top: 1px solid #f2f3f5;
  border-bottom: 1px solid #f2f3f5;
}

.filter-item {
  padding: 12px;
  box-sizing: border-box;
  font-size: 14px;
}

.filter-item:first-child {
  border-right: 1px solid #f2f3f5;
}

.filter-label {
  color: #646566;
  margin-right: 6px;
}

.filter-value {
  color: #1989fa;
}

.search-actions {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  padding: 12px;
}

.add-btn {
  margin-bottom: 10px;
}

.summary-row {
  color: #909399;
  font-size: 13px;
  margin: 4px 0 10px;
}

.student-card {
  background: #fff;
  border-radius: 12px;
  padding: 14px;
  margin-bottom: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
}

.card-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 10px;
}

.student-main {
  flex: 1;
  min-width: 0;
}

.student-name {
  color: #303133;
  font-size: 17px;
  font-weight: bold;
  margin-bottom: 5px;
}

.student-username {
  color: #909399;
  font-size: 13px;
}

.info-line {
  color: #606266;
  font-size: 13px;
  line-height: 24px;
  word-break: break-all;
}

.info-line span {
  color: #909399;
}

.action-grid {
  margin-top: 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.pagination-box {
  margin: 16px 0 4px;
  background: #fff;
  border-radius: 12px;
  padding: 8px 0;
  overflow: hidden;
}

.popup-content {
  padding: 18px 14px 22px;
}

.popup-title {
  text-align: center;
  color: #303133;
  font-size: 17px;
  font-weight: bold;
  margin-bottom: 18px;
}

.submit-area {
  margin-top: 24px;
}
</style>